// Public surface for GTM event tracking. Components/plugins import from here.
// Contract source: Docs/07-gtm-events-copy/01-GTM-Events-Requirements.md.

import { EVENT_NAMES, BUTTON_LOCATIONS, LEAD_TYPES } from './constants'
import {
  getUserProperty,
  determineContentGroups,
  determineBlogContentGroups,
  determinePageType,
  hashEmail,
  hashPhone,
  stripHtmlTags,
  computeReadingDuration,
} from './helpers'
import {
  setSlugPageTypeMap,
  buildSlugPageTypeMap,
  setBlogDetailSlugs,
  buildBlogDetailSlugSet,
  isBlogDetailSlug,
} from './page-type-overrides'
// import { saveEventToStorage } from './storage' // uncomment to enable debug log

export {
  hashEmail,
  hashPhone,
  determinePageType,
  computeReadingDuration,
  setSlugPageTypeMap,
  buildSlugPageTypeMap,
  setBlogDetailSlugs,
  buildBlogDetailSlugSet,
  isBlogDetailSlug,
}
export {
  getStoredEvents,
  clearStoredEvents,
  getStoredEventsByType,
  getLastNEvents,
  printStoredEvents,
} from './storage'

function pushEventToDataLayer(eventData) {
  if (typeof window === 'undefined') return false
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push(eventData)
  // saveEventToStorage(eventData.event, eventData)
  return true
}

/* ===== Page & Navigation ===== */

export function pushPageDescriptionEvent(path, pageProps) {
  if (typeof window === 'undefined') return false
  if (path === undefined || path === null) {
    console.warn('[GTM] page_description: missing path, event not sent')
    return false
  }
  return pushEventToDataLayer({
    event: EVENT_NAMES.PAGE_DESCRIPTION,
    content_groups: determineContentGroups(path, pageProps),
    user_property: getUserProperty(),
  })
}

// Blog detail pages override content_groups but keep the `page_description`
// event name per the SEO contract (01-Requirements.md §Page Description).
export function pushBlogPageDescriptionEvent(blogTitle, blogCategory) {
  if (typeof window === 'undefined') return false
  if (!blogTitle) {
    console.warn('[GTM] page_description (blog): missing blogTitle, event not sent')
    return false
  }
  return pushEventToDataLayer({
    event: EVENT_NAMES.PAGE_DESCRIPTION,
    content_groups: determineBlogContentGroups(blogTitle, blogCategory),
    user_property: getUserProperty(),
  })
}

export function pushVirtualPageViewEvent(pageTitle, pageUrl, referrerUrl) {
  if (typeof window === 'undefined') return false
  if (!pageTitle || !pageUrl) {
    console.warn('[GTM] virtual_page_view: missing required params', {
      pageTitle: !!pageTitle,
      pageUrl: !!pageUrl,
    })
    return false
  }
  return pushEventToDataLayer({
    event: EVENT_NAMES.VIRTUAL_PAGE_VIEW,
    page_title: pageTitle,
    page_url: pageUrl,
    referrer_url: referrerUrl || '',
  })
}

export function pushBlogViewedEvent(blogName, blogCategory, blogWriter, blogReadingDuration) {
  if (typeof window === 'undefined') return false
  if (!blogName || !blogCategory || !blogWriter || !blogReadingDuration) {
    console.warn('[GTM] blog_viewed: missing required params', {
      blogName: !!blogName,
      blogCategory: !!blogCategory,
      blogWriter: !!blogWriter,
      blogReadingDuration: !!blogReadingDuration,
    })
    return false
  }
  return pushEventToDataLayer({
    event: EVENT_NAMES.BLOG_VIEWED,
    blog_name: blogName,
    blog_category: blogCategory,
    blog_writer: blogWriter,
    blog_reading_duration: blogReadingDuration,
  })
}

/* ===== Button Clicks ===== */

export function pushTopHeaderButtonClickEvent(buttonName, pageTypeOverride) {
  if (typeof window === 'undefined') return false
  if (!buttonName) {
    console.warn('[GTM] button_click (header_top): missing buttonName')
    return false
  }
  return pushEventToDataLayer({
    event: EVENT_NAMES.BUTTON_CLICK,
    button_name: buttonName,
    button_location: BUTTON_LOCATIONS.HEADER_TOP,
    page_type: determinePageType(window.location.pathname, pageTypeOverride),
  })
}

export function pushHeaderButtonClickEvent(buttonName, pageTypeOverride) {
  if (typeof window === 'undefined') return false
  if (!buttonName) {
    console.warn('[GTM] button_click (header): missing buttonName')
    return false
  }
  return pushEventToDataLayer({
    event: EVENT_NAMES.BUTTON_CLICK,
    button_name: buttonName,
    button_location: BUTTON_LOCATIONS.HEADER,
    page_type: determinePageType(window.location.pathname, pageTypeOverride),
  })
}

export function pushNavigationMenuClickEvent(category1, category2, category3, pageTypeOverride) {
  if (typeof window === 'undefined') return false
  if (!category1) {
    console.warn('[GTM] button_click (navigation_menu): missing category1')
    return false
  }
  return pushEventToDataLayer({
    event: EVENT_NAMES.BUTTON_CLICK,
    button_location: BUTTON_LOCATIONS.NAVIGATION_MENU,
    category1,
    category2: category2 || '',
    category3: category3 || '',
    page_type: determinePageType(window.location.pathname, pageTypeOverride),
  })
}

export function pushFooterMenuClickEvent(category1, category2, category3, pageTypeOverride) {
  if (typeof window === 'undefined') return false
  if (!category1) {
    console.warn('[GTM] button_click (footer_menu): missing category1')
    return false
  }
  return pushEventToDataLayer({
    event: EVENT_NAMES.BUTTON_CLICK,
    button_location: BUTTON_LOCATIONS.FOOTER_MENU,
    category1,
    category2: category2 || '',
    category3: category3 || '',
    page_type: determinePageType(window.location.pathname, pageTypeOverride),
  })
}

export function pushButtonClickEvent(buttonName, buttonLocation, pageTypeOverride) {
  if (typeof window === 'undefined') return false
  if (!buttonName || !buttonLocation) {
    console.warn('[GTM] button_click: missing buttonName or buttonLocation', {
      buttonName: !!buttonName,
      buttonLocation: !!buttonLocation,
    })
    return false
  }
  return pushEventToDataLayer({
    event: EVENT_NAMES.BUTTON_CLICK,
    button_name: buttonName,
    button_location: buttonLocation,
    page_type: determinePageType(window.location.pathname, pageTypeOverride),
  })
}

export function pushBlogButtonClickEvent(buttonName) {
  if (typeof window === 'undefined') return false
  if (!buttonName) {
    console.warn('[GTM] blog_button_click: missing buttonName')
    return false
  }
  return pushEventToDataLayer({
    event: EVENT_NAMES.BLOG_BUTTON_CLICK,
    button_location: BUTTON_LOCATIONS.BLOG_CATEGORIES,
    button_name: buttonName,
  })
}

export function pushViewSearchResultsEvent(searchTerm, buttonLocation) {
  if (typeof window === 'undefined') return false
  if (!searchTerm) {
    console.warn('[GTM] view_search_results: missing searchTerm')
    return false
  }
  return pushEventToDataLayer({
    event: EVENT_NAMES.VIEW_SEARCH_RESULTS,
    button_location: buttonLocation || BUTTON_LOCATIONS.BLOG_SEARCH,
    search_term: searchTerm,
  })
}

/* ===== Lead & Form Events ===== */

export function sendLeadViewEvent(leadType, pageTypeOverride) {
  if (typeof window === 'undefined') return false
  if (!leadType) {
    console.warn('[GTM] lead_view: missing leadType')
    return false
  }
  return pushEventToDataLayer({
    event: EVENT_NAMES.LEAD_VIEW,
    lead_type: stripHtmlTags(leadType),
    page_type: determinePageType(window.location.pathname, pageTypeOverride),
  })
}

export async function sendLeadSuccessEvent(leadType, email, phone, pageTypeOverride) {
  if (typeof window === 'undefined') return false
  if (!leadType || !email || !phone) {
    console.warn('[GTM] lead_success: missing required params', {
      leadType: !!leadType,
      email: !!email,
      phone: !!phone,
    })
    return false
  }
  const [hashed_email, hashed_phone_number] = await Promise.all([
    hashEmail(email),
    hashPhone(phone),
  ])
  return pushEventToDataLayer({
    event: EVENT_NAMES.LEAD_SUCCESS,
    lead_type: stripHtmlTags(leadType),
    page_type: determinePageType(window.location.pathname, pageTypeOverride),
    hashed_email,
    hashed_phone_number,
  })
}

export function sendLeadRedirectEvent(leadType, pageTypeOverride) {
  if (typeof window === 'undefined') return false
  if (!leadType) {
    console.warn('[GTM] lead_redirect: missing leadType')
    return false
  }
  return pushEventToDataLayer({
    event: EVENT_NAMES.LEAD_REDIRECT,
    lead_type: stripHtmlTags(leadType),
    page_type: determinePageType(window.location.pathname, pageTypeOverride),
  })
}

export function pushLeadSubscriptionEvent(pageTypeOverride) {
  if (typeof window === 'undefined') return false
  return pushEventToDataLayer({
    event: EVENT_NAMES.LEAD_SUBSCRIPTION,
    lead_type: LEAD_TYPES.SUBSCRIPTION,
    page_type: determinePageType(window.location.pathname, pageTypeOverride),
  })
}

export function pushFormViewEvent(formType, pageTypeOverride) {
  if (typeof window === 'undefined') return false
  if (!formType) {
    console.warn('[GTM] form_view: missing formType')
    return false
  }
  return pushEventToDataLayer({
    event: EVENT_NAMES.FORM_VIEW,
    form_type: formType,
    page_type: determinePageType(window.location.pathname, pageTypeOverride),
  })
}

export function pushFormSelectEvent(formType, step, answer, pageTypeOverride) {
  if (typeof window === 'undefined') return false
  if (!formType || !step) {
    console.warn('[GTM] form_select: missing formType or step', {
      formType: !!formType,
      step: !!step,
    })
    return false
  }
  return pushEventToDataLayer({
    event: EVENT_NAMES.FORM_SELECT,
    form_type: formType,
    step,
    answer: answer || '',
    page_type: determinePageType(window.location.pathname, pageTypeOverride),
  })
}

export function pushFormResultEvent(formType, pageTypeOverride) {
  if (typeof window === 'undefined') return false
  if (!formType) {
    console.warn('[GTM] form_result: missing formType')
    return false
  }
  return pushEventToDataLayer({
    event: EVENT_NAMES.FORM_RESULT,
    form_type: formType,
    page_type: determinePageType(window.location.pathname, pageTypeOverride),
  })
}
